/**
 * Hanlyn product media gallery
 *
 * One custom element, two modes:
 *   Desktop (>= 990px): vertical thumbnail column selects a single large stage
 *     image. Prev/next arrows step through the same list and wrap around.
 *   Mobile  (<= 989px): the original full-bleed horizontal scroll carousel
 *     with dot navigation. Arrows clamp at the ends, as before.
 *
 * Markup comes from snippets/product-media-gallery-grid.liquid. Read the
 * comment block at the top of that snippet before changing selectors here.
 *
 * Variant switching: assets/product-info.js -> updateMedia() only knows how to
 * drive `<media-gallery><ul><li data-media-id>`, which this theme does not
 * emit, so it is a no-op for the gallery and always has been. This element
 * subscribes to PUB_SUB_EVENTS.variantChange instead and activates the media
 * matching `variant.featured_media.id`. Every product media is rendered up
 * front, so nothing needs to be inserted or removed at runtime.
 */

if (!customElements.get('product-media-gallery-carousel')) {
  customElements.define(
    'product-media-gallery-carousel',
    class ProductMediaGalleryCarousel extends HTMLElement {
      constructor() {
        super();
        this.currentIndex = 0;
        this.desktopQuery = window.matchMedia('(min-width: 990px)');
      }

      connectedCallback() {
        // Defer a tick: the element's own children are parsed by now, but the
        // section may still be mid-swap during a variant-driven view
        // transition.
        this.initTimer = setTimeout(() => this.init(), 0);
      }

      disconnectedCallback() {
        clearTimeout(this.initTimer);
        this.teardown();
      }

      get isDesktop() {
        return this.desktopQuery.matches;
      }

      init() {
        this.container = this.querySelector('.hanlyn-product-media-grid__container');
        this.items = Array.from(this.querySelectorAll('.hanlyn-product-media-grid__item'));
        this.thumbsTrack = this.querySelector('.hanlyn-pgallery__thumbs-track');
        this.thumbs = Array.from(this.querySelectorAll('.hanlyn-pgallery__thumb'));
        this.dots = Array.from(this.querySelectorAll('.hanlyn-product-carousel__dot'));
        this.prevButton = this.querySelector('.hanlyn-product-carousel__arrow--prev');
        this.nextButton = this.querySelector('.hanlyn-product-carousel__arrow--next');
        this.mediaCount = this.items.length;

        this.subscribeToVariantChange();

        if (!this.container || this.mediaCount === 0) return;

        const activeIndex = this.items.findIndex((item) => item.classList.contains('is-active'));
        this.currentIndex = activeIndex > -1 ? activeIndex : 0;

        if (this.mediaCount <= 1) return;

        this.handleScroll = this.handleScroll.bind(this);
        this.handleThumbClick = this.handleThumbClick.bind(this);
        this.handleThumbKeydown = this.handleThumbKeydown.bind(this);
        this.handleDotClick = this.handleDotClick.bind(this);
        this.handlePrevClick = this.handlePrevClick.bind(this);
        this.handleNextClick = this.handleNextClick.bind(this);
        this.handleBreakpointChange = this.handleBreakpointChange.bind(this);

        this.container.addEventListener('scroll', this.handleScroll, { passive: true });

        this.thumbs.forEach((thumb) => {
          thumb.addEventListener('click', this.handleThumbClick);
          thumb.addEventListener('keydown', this.handleThumbKeydown);
        });

        this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));

        if (this.prevButton) this.prevButton.addEventListener('click', this.handlePrevClick);
        if (this.nextButton) this.nextButton.addEventListener('click', this.handleNextClick);

        if (this.desktopQuery.addEventListener) {
          this.desktopQuery.addEventListener('change', this.handleBreakpointChange);
        }

        this.select(this.currentIndex, { scrollStage: false });
      }

      teardown() {
        this.variantUnsubscriber?.();
        this.variantUnsubscriber = null;

        this.container?.removeEventListener('scroll', this.handleScroll);

        this.thumbs?.forEach((thumb) => {
          thumb.removeEventListener('click', this.handleThumbClick);
          thumb.removeEventListener('keydown', this.handleThumbKeydown);
        });

        this.dots?.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));

        this.prevButton?.removeEventListener('click', this.handlePrevClick);
        this.nextButton?.removeEventListener('click', this.handleNextClick);

        if (this.desktopQuery.removeEventListener && this.handleBreakpointChange) {
          this.desktopQuery.removeEventListener('change', this.handleBreakpointChange);
        }
      }

      /* --- selection ------------------------------------------------------ */

      select(index, { scrollStage = true, focusThumb = false } = {}) {
        if (index < 0 || index >= this.mediaCount) return;

        this.currentIndex = index;

        this.items.forEach((item, i) => {
          const isActive = i === index;
          item.classList.toggle('is-active', isActive);
          // Only meaningful on desktop, where inactive stage items are
          // visibility:hidden. On mobile every item stays on screen, so do not
          // mark any of them hidden to assistive tech.
          if (this.isDesktop) {
            item.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          } else {
            item.removeAttribute('aria-hidden');
          }
        });

        this.thumbs.forEach((thumb, i) => {
          const isActive = i === index;
          thumb.classList.toggle('is-active', isActive);
          thumb.setAttribute('aria-selected', isActive ? 'true' : 'false');
          thumb.tabIndex = isActive ? 0 : -1;
        });

        this.dots.forEach((dot, i) => {
          const isActive = i === index;
          dot.classList.toggle('hanlyn-product-carousel__dot--active', isActive);
          dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        if (this.isDesktop) {
          this.scrollThumbIntoView(this.thumbs[index]);
        } else if (scrollStage) {
          this.scrollStageToIndex(index);
        }

        if (focusThumb) this.thumbs[index]?.focus();
      }

      setActiveMedia(mediaId) {
        if (!mediaId) return;
        const index = this.items.findIndex((item) => item.dataset.mediaId === String(mediaId));
        if (index > -1) this.select(index);
      }

      /* --- scrolling ------------------------------------------------------ */

      scrollStageToIndex(index) {
        const item = this.items[index];
        if (!item || !this.container) return;
        // scrollLeft rather than scrollIntoView: scrollIntoView on a horizontal
        // scroller also nudges the page vertically on some browsers.
        this.container.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
      }

      // Manual maths rather than scrollIntoView({block:'nearest'}), which can
      // scroll the window as well as the thumb track.
      scrollThumbIntoView(thumb) {
        const track = this.thumbsTrack;
        if (!track || !thumb) return;

        const top = thumb.offsetTop;
        const bottom = top + thumb.offsetHeight;

        if (top < track.scrollTop) {
          track.scrollTo({ top, behavior: 'smooth' });
        } else if (bottom > track.scrollTop + track.clientHeight) {
          track.scrollTo({ top: bottom - track.clientHeight, behavior: 'smooth' });
        }
      }

      /* --- handlers ------------------------------------------------------- */

      handleScroll() {
        if (this.isDesktop || !this.container || !this.items[0]) return;

        const itemWidth = this.items[0].offsetWidth;
        if (!itemWidth) return;

        const newIndex = Math.round(this.container.scrollLeft / itemWidth);
        if (newIndex !== this.currentIndex && newIndex >= 0 && newIndex < this.mediaCount) {
          this.select(newIndex, { scrollStage: false });
        }
      }

      handleThumbClick(event) {
        const index = parseInt(event.currentTarget.dataset.index, 10);
        if (!Number.isNaN(index)) this.select(index);
      }

      handleThumbKeydown(event) {
        const keys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight', 'Home', 'End'];
        if (!keys.includes(event.key)) return;

        event.preventDefault();

        let next = this.currentIndex;
        if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          next = (this.currentIndex - 1 + this.mediaCount) % this.mediaCount;
        } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          next = (this.currentIndex + 1) % this.mediaCount;
        } else if (event.key === 'Home') {
          next = 0;
        } else if (event.key === 'End') {
          next = this.mediaCount - 1;
        }

        this.select(next, { focusThumb: true });
      }

      handleDotClick(event) {
        const index = parseInt(event.currentTarget.dataset.index, 10);
        if (!Number.isNaN(index)) this.select(index);
      }

      // Desktop wraps around; mobile clamps, matching the previous behaviour.
      handlePrevClick() {
        const next = this.isDesktop
          ? (this.currentIndex - 1 + this.mediaCount) % this.mediaCount
          : Math.max(0, this.currentIndex - 1);
        this.select(next);
      }

      handleNextClick() {
        const next = this.isDesktop
          ? (this.currentIndex + 1) % this.mediaCount
          : Math.min(this.mediaCount - 1, this.currentIndex + 1);
        this.select(next);
      }

      handleBreakpointChange() {
        // Re-apply state for the new mode: aria-hidden flips, and crossing to
        // mobile needs the scroller parked on the active item.
        this.select(this.currentIndex);
      }

      /* --- variant switching ---------------------------------------------- */

      subscribeToVariantChange() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        if (this.variantUnsubscriber) return;

        this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
          if (!data) return;

          // Ignore events published by a different product section on the page
          // (featured product, quick order list, and so on).
          const productInfo = this.closest('product-info');
          const ownSection = productInfo
            ? productInfo.dataset.originalSection || productInfo.dataset.section
            : null;
          if (data.sectionId && ownSection && data.sectionId !== ownSection) return;

          const featuredMediaId = data.variant?.featured_media?.id;
          if (!featuredMediaId) return;

          this.setActiveMedia(`${this.dataset.sectionId}-${featuredMediaId}`);
        });
      }
    }
  );
}
